import { yupResolver } from '@hookform/resolvers/yup';
import { SendHorizonal } from 'lucide-react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import posthog from 'posthog-js';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { Button } from '@/components/button';
import { TextInput } from '@/components/inputs';
import { UserMessage } from '@/components/ui/user-message';
import { useNotifications } from '@/hooks/use-notifications';
import { fluentProvider } from '@/lib/api/providers/fluent';
import { INLINE_CONTACT_FORM_TYPES, USER_MESSAGES } from '@/lib/constants';
import { InlineContactFormType } from '@/types';
import { getUserOrg } from '@/utils/permissions/helpers';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

export interface ContactFormInterface {
  email: string;
  body: string;
  messageType: InlineContactFormType;
}

export const feedbackValidationSchema = yup.object().shape({
  messageType: yup.string(),
  body: yup.string().required('Enter a message'),
  email: yup.string().email('Invalid email address'),
});

interface AppNavigationContactFormProps {
  type: InlineContactFormType;
}

const AppNavigationContactForm = ({ type = INLINE_CONTACT_FORM_TYPES.SUPPORT }: AppNavigationContactFormProps) => {
  const { data: session } = useSession();
  const [messageSent, setMessageSent] = useState(false);
  const router = useRouter();
  const { addNotification } = useNotifications();

  const user = session?.user;
  const userCurrentOrg = session ? getUserOrg(session?.user)?.name : undefined;
  // eslint-disable-next-line no-underscore-dangle
  const postHogUserId = posthog?.__loaded ? posthog?.get_distinct_id() : 'unknown';

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormInterface>({
    values: {
      messageType: type || '',
      body: '',
      email: user?.email || '',
    },
    resolver: yupResolver(feedbackValidationSchema),
  });

  const handleFormSubmit = async ({ messageType, body, email }: ContactFormInterface) => {
    try {
      await fluentProvider.sendContactEmail({
        messageType,
        body,
        email,
        meta: {
          url: router.asPath,
          organisation: userCurrentOrg || '',
          postHogUserId,
        },
      });

      setMessageSent(true);
      trackUser.event('Submit support request');

      reset();
    } catch (error: any) {
      addNotification({
        heading: 'Error sending message',
        description: `There was an issue sending your message. ${USER_MESSAGES.TRY_AGAIN}`,
        variant: 'error',
      });
    }
  };

  const { form, formActions, hiddenFields } = styles();

  return messageSent ? (
    <UserMessage
      className="!mx-0 !my-6 w-full"
      noIcon
      variant="success"
      heading="Thanks!"
      body="Your message has been successfully submitted."
    />
  ) : (
    <form onSubmit={handleSubmit(handleFormSubmit)} className={form()}>
      <ol>
        <li>
          <Controller
            control={control}
            name="body"
            defaultValue=""
            render={({ field }) => (
              <TextInput
                {...field}
                label="Your message"
                placeholder="Please enter your message here and remember to include as much detail as possible."
                errors={errors}
                rows="7"
                isTextArea
                disabled={isSubmitting}
              />
            )}
          />
        </li>
        <li className={hiddenFields()}>
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <TextInput {...field} label="Email address" errors={errors} isOptional disabled={isSubmitting} />
            )}
          />
          <Controller
            control={control}
            name="messageType"
            render={({ field }) => (
              <TextInput {...field} label="message type" errors={errors} isOptional disabled={isSubmitting} />
            )}
          />
        </li>
        <li className={formActions()}>
          <Button
            type="submit"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            iconLeft={<SendHorizonal className="size-4" />}
          >
            Submit
          </Button>
        </li>
      </ol>
    </form>
  );
};

const styles = tv({
  slots: {
    form: 'mt-6 [&_li]:mb-4 last:[&_li]:mb-0 [&_ol]:mb-4',
    formActions: 'mb-4',
    footerText: 'm-0 text-sm [&_a]:text-lightBlue-600',
    hiddenFields: 'hidden',
  },
});

export { AppNavigationContactForm };
